import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';

import {
  SR_API_ACTION_FRONT_GET_CONVERSATION_MESSAGES,
  SR_API_ACTION_FRONT_GET_CONVERSATION_COMMENTS,
} from 'api/actionTypes';

import SrDarkModal from 'client/components/SrDarkModal';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrFrontConversationForm from 'client/components/SrFrontConversationForm';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';
import postWithState from 'client/actions/postWithState';
import branch from 'client/core/branch';

function makeTimeline({ user, frontComments, frontMessages }) {
  const timeline = [
    ...frontMessages.map((frontMessage) => ({
      id: frontMessage.id,
      author: frontMessage.is_inbound
        ? user.name
        : (frontMessage.author
          ? `${frontMessage.author.first_name} ${frontMessage.author.last_name}`
          : 'Sunroom Customer Number'),
      phone: frontMessage.recipients.find(({ role }) => role === 'from').handle,
      createdAtDate: moment.unix(frontMessage.created_at).format('MMM D, h:mm A'),
      body: (
        <span>{frontMessage.text}</span>
      ),
      date: frontMessage.created_at * 1000,
      isMessage: true,
      isInbound: frontMessage.is_inbound,
    })),
    ...frontComments.map((frontComment) => ({
      id: frontComment.id,
      author: frontComment.author
        ? `${frontComment.author.first_name} ${frontComment.author.last_name}`
        : 'Sunroom App',
      createdAtDate: moment.unix(frontComment.posted_at).format('MMM D, h:mm A'),
      /* eslint-disable react/no-array-index-key */
      body: (
        <div className='bubble'>
          {frontComment.body.split('\n').map((text, i) => (<span key={i}>{text}</span>))}
        </div>
      ),
      /* eslint-enable react/no-array-index-key */
      date: frontComment.posted_at * 1000,
      isComment: true,
    })),
  ];

  timeline.sort((a, b) => b.date - a.date);

  return timeline.reverse();
}

@branch({
  currentUser: ['currentUser'],
})
class SrFrontConversationModal extends Component {
  static propTypes = {
    frontConversation: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      frontMessages: [],
      frontComments: [],
      inProgress: true,
    };
  }

  componentDidMount() {
    this._loadData();
  }

  _loadData = () => {
    const {
      dispatch,
      frontConversation,
    } = this.props;

    Promise.all([
      dispatch(postWithState, {
        action: SR_API_ACTION_FRONT_GET_CONVERSATION_MESSAGES,
        payload: {
          frontConversationId: frontConversation.conversationId,
          count: 100,
        },
      }),
      dispatch(postWithState, {
        action: SR_API_ACTION_FRONT_GET_CONVERSATION_COMMENTS,
        payload: {
          frontConversationId: frontConversation.conversationId,
        },
      }),
    ]).then(([frontMessages, frontComments]) => {
      if (!frontMessages.length) {
        this.setState({
          inProgress: false,
        });
        return;
      }

      const lastFrontMessageCreatedAt = frontMessages[frontMessages.length - 1].created_at * 1000;

      this.setState({
        frontMessages,
        frontComments: frontComments.filter((frontComment) => (
          (frontComment.posted_at * 1000) > lastFrontMessageCreatedAt
        )),
        inProgress: false,
      }, () => {
        const element = document.getElementById('bottom');
        element.scrollIntoView();
      });
    });
  }

  _onMessageSent = (newFrontMessage) => {
    this.setState({
      frontMessages: [
        ...this.state.frontMessages,
        newFrontMessage,
      ],
    }, () => {
      const element = document.getElementById('bottom');
      element.scrollIntoView();
    });
  }

  render() {
    const {
      onClose,
      user,
      frontConversation,
      dispatch,
      currentUser,
    } = this.props;
    const {
      frontMessages,
      frontComments,
      inProgress,
    } = this.state;

    const timeline = makeTimeline({
      user,
      frontMessages,
      frontComments,
    });

    return (
      <SrDarkModal onClose={onClose}>
        <div
          onClick={preventDefaultPropagation}
          className='adminDefaultModal SrFrontConversationModal'
        >
          <div className='frontConversationContainer'>
            <div className='timeline'>
              {inProgress && (
                <ActivityIndicator />
              )}
              {timeline.map((activity, activityIndex) => {
                const previousActivity = timeline[activityIndex - 1] || {};
                const isSameAuthor = (
                  previousActivity.author === activity.author
                  && previousActivity.isComment === activity.isComment
                );
                return (
                  <div
                    key={activity.id}
                    className={classnames('messageContainer', {
                      message: activity.isMessage,
                      comment: activity.isComment,
                      isSameAuthor: isSameAuthor,
                      isInbound: activity.isInbound,
                    })}
                  >
                    {!isSameAuthor && (
                      <div className='header'>
                        <span className='author'>
                          {activity.author}
                          {activity.isInbound && (
                            <span className='phone'>{activity.phone}</span>
                          )}
                        </span>
                        <span className='createdAt'>{activity.createdAtDate}</span>
                      </div>
                    )}
                    <div className='textContainer'>
                      {isSameAuthor && (
                        <span className='createdAt'>{activity.createdAtDate}</span>
                      )}
                      {activity.body}
                    </div>
                  </div>
                );
              })}
              <div id='bottom' />
            </div>
            {!inProgress && (frontConversation.channelId || user.frontChannelId) && (
              <SrFrontConversationForm
                frontConversation={frontConversation}
                user={user}
                dispatch={dispatch}
                onMessageSent={this._onMessageSent}
                currentUser={currentUser}
              />
            )}
          </div>
        </div>
      </SrDarkModal>
    );
  }
}

SrFrontConversationModal.displayName = 'SrFrontConversationModal';

export default SrFrontConversationModal;
